// ^^^ See the single and multiple skill level change icon fixtures
/**
 * Visual summary of one or more skill level changes.
 */

import * as React from "react";

import {Body} from "@khanacademy/wonder-blocks-typography";
import {logUnreachableCode} from "@khan/logging";

// TODO: How do I import from this dir?
// I don't want to copy over these functions
// eslint-disable-next-line static-service/require-shared-package-imports-only -- move to shared package
import {getLevelChange} from "~/mastery-utils-package/mastery-utils.ts";

import type {
    ActivitySessionsV2Query_user_activityLogV2_activitySessions_sessions as ActivitySessionsReportRow,
    ActivitySessionsV2Query_user_activityLogV2_activitySessions_sessions_MasteryActivitySession_skillLevels as SkillLevelChange,
} from "./__graphql-types__/ActivitySessionsV2Query.ts";
import ChangeIndicator from "./change-indicator.tsx";

const renderMultipleMasterySkillLevelChanges = (
    skillLevelChanges: Array<SkillLevelChange | null | undefined>,
) => {
    const positiveChanges = skillLevelChanges.filter(
        (skillLevelChange) =>
            getLevelChange(skillLevelChange?.before, skillLevelChange?.after) >
            0,
    ).length;
    const negativeChanges = skillLevelChanges.length - positiveChanges;

    if (positiveChanges === 0) {
        return <Body>{`- ${negativeChanges}`}</Body>;
    }
    if (negativeChanges === 0) {
        return <Body>{`+ ${positiveChanges}`}</Body>;
    }
    return <Body>{`+ ${positiveChanges} / - ${negativeChanges}`}</Body>;
};

const MasterySkillLevelChangeIcon = ({
    skillLevelChanges,
}: {
    skillLevelChanges:
        | Array<SkillLevelChange | null | undefined>
        | null
        | undefined;
}): React.ReactElement => {
    if (
        !skillLevelChanges?.length ||
        !skillLevelChanges[0]?.before ||
        !skillLevelChanges[0]?.after
    ) {
        return <ChangeIndicator change={0} />;
    }

    if (skillLevelChanges?.length > 1) {
        return renderMultipleMasterySkillLevelChanges(skillLevelChanges);
    }

    return (
        <ChangeIndicator
            change={getLevelChange(
                skillLevelChanges[0].before,
                skillLevelChanges[0].after,
            )}
        />
    );
};

export const MultipleSkillLevelChangeIcon = ({
    activitySession,
}: {
    activitySession: ActivitySessionsReportRow;
}): React.ReactElement | null => {
    switch (activitySession.__typename) {
        case "BasicActivitySession":
            return <ChangeIndicator change={0} />;
        case "MasteryActivitySession":
            return (
                <MasterySkillLevelChangeIcon
                    skillLevelChanges={activitySession.skillLevels}
                />
            );
        default:
            logUnreachableCode(
                // @ts-expect-error - TS2339 - Property '__typename' does not exist on type 'never'.
                `Unknown ActivitySession type: ${activitySession.__typename}`,
                // @ts-expect-error - TS2339 - Property '__typename' does not exist on type 'never'.
                activitySession.__typename,
            );
            return null;
    }
};

export const SingleSkillLevelChangeIcon = ({
    skillLevelChange,
}: {
    skillLevelChange: SkillLevelChange;
}): React.ReactElement | null => {
    switch (skillLevelChange.__typename) {
        case "SkillLevelChange":
            return (
                <MasterySkillLevelChangeIcon
                    skillLevelChanges={[skillLevelChange]}
                />
            );
        default:
            logUnreachableCode(
                `Unknown Skill Level type: ${skillLevelChange.__typename}`,
                skillLevelChange.__typename,
            );
            return null;
    }
};
